/**
 * AI animals.
 *
 * Kate: "in single player, the animals, all of them are AI. They're just AI people
 * playing the game."
 *
 * On 2026-08-17, having played it, she reversed her no-hostile-mobs rule: "the mobs have
 * to be hostile. They need to kill you if you're playing offline. The mobs have to, like,
 * aggravate you." So offline they hunt the player. Online they go back to wandering,
 * because there they stand in for other players.
 *
 * Difficulty scales them, per "you can choose your difficulty. Depending on your
 * difficulty, the harder the game gets."
 */
import * as THREE from 'three'
import type { BiteTarget } from './combat'
import { MAX_HEARTS } from '../player/survival'
import { SPECIES, type SpeciesId } from './species'
import { hunts, IDENTITY, noticedRange } from './identity'
import { animate, buildAnimal, gaitFor, updateFurDetail, type AnimalModel } from './models'
import { clampToBorder } from '../world/map'
import { heightAt } from '../world/terrain'

export type Difficulty = 'easy' | 'normal' | 'hard'

/** Difficulty multiplies how much punishment an AI animal soaks up. */
const TOUGHNESS: Record<Difficulty, number> = { easy: 0.6, normal: 1, hard: 1.6 }
/** How far a hostile animal notices you from, by difficulty. */
const AGGRO_RANGE: Record<Difficulty, number> = { easy: 9, normal: 15, hard: 24 }
/** Hearts per second it takes off you while it is on you. */
const AGGRO_DAMAGE: Record<Difficulty, number> = { easy: 0.5, normal: 1, hard: 1.8 }
/** How close it has to be to bite. */
const ATTACK_RANGE = 2.2
/** It stops here rather than walking into the player. */
const STANDOFF = 1.7
/** Animals shove apart within this distance, so a pack does not become one heap. */
const PERSONAL_SPACE = 2.4
/** Radians per second an animal can turn. Snapping the heading is what made it a magnet. */
const TURN_RATE = 1.6
/** Seconds it hesitates after noticing you, before committing to the chase. */
const NOTICE_DELAY: Record<Difficulty, number> = { easy: 1.6, normal: 1.0, hard: 0.45 }
/** Once interested, it keeps coming this much further than the distance it noticed you at. */
const PURSUIT_SLACK = 8
/**
 * Prey bolts at this fraction of the range it can notice at. Without it the deer, whose
 * senses beat a wolf's, always fled before the wolf ever committed — so no hunt in the
 * world ever completed and the ecosystem never actually did anything.
 */
const FLEE_TRIGGER = 0.55
/** A threat feels this much closer than it is, so danger wins ties without dominating. */
const THREAT_BIAS = 0.45
/**
 * Pack hunting. Kate gave the wolf strength, endurance AND speed, and the sniff trail
 * that leads to prey — an animal built to run something down with others. A wolf that
 * sees a packmate committed to a target joins that hunt rather than picking its own.
 *
 * This is the cheapest drama in the game: three wolves converging on one deer from
 * different angles is a scene nobody wrote.
 */
const PACK_SPECIES = new Set(['wolf', 'dog'])
/** How far a packmate's hunt is worth joining. */
const PACK_RANGE = 34
/** A fleeing animal tires. A persistence hunter is how a slower wolf catches a deer. */
const STAMINA_SECONDS = 6
const TIRED_SPEED = 0.62

/** How long it will chase before giving up and going back to wandering. */
const PURSUIT_PATIENCE: Record<Difficulty, number> = { easy: 8, normal: 14, hard: 22 }

/**
 * What a packmate is hunting. Either a real agent, or a stand-in for the player — the
 * player is not an agent, but a pack still needs to converge on them.
 */
export type PackTarget =
  | AnimalAgent
  | { x: number; z: number; speciesId: SpeciesId; isPlayerProxy: true; isDead(): boolean }

/** Whatever an animal is currently reacting to — a meal, or something that eats it. */
interface Focus {
  x: number
  z: number
  species: SpeciesId
  /** The animal it is focused on, or null when the focus is the player. */
  agent: AnimalAgent | null
  /** True when this focus hunts us, rather than the other way round. */
  threat: boolean
}

export class AnimalAgent implements BiteTarget {
  readonly id: string
  readonly object = new THREE.Group()
  speciesId: SpeciesId
  hearts: number
  x: number
  z: number

  private readonly model: AnimalModel
  private stridePhase = 0
  private heading: number
  /** True while chasing the player. Only ever set offline. */
  hunting = false
  private readonly difficulty: Difficulty
  /**
   * What it is currently doing. Modelled as a state machine because the earlier version
   * recomputed flags every frame, which let it wander out of range mid-hesitation and
   * lose interest before it ever committed — it would notice you and then walk away.
   */
  private mood: 'wander' | 'noticing' | 'chasing' | 'bored' | 'fleeing' = 'wander'
  /** What the player currently is, so this animal can decide how to feel about them. */
  preySpecies: SpeciesId | null = null
  /** 0..1 — how hidden the player is right now (frozen or camouflaged). */
  preyHidden = 0
  /** Multiplier on senses after dark — predators hunt harder at night. */
  nightBoost = 1
  private boredTimer = 0
  /**
   * What this animal is currently hunting, for packmates to join. Either another agent,
   * or a lightweight stand-in for the player.
   */
  packTarget: PackTarget | null = null

  /** How long the player has been hidden while this animal was chasing them. */
  private lostTimer = 0
  /** How long this animal has been running. It tires, which is what lets a hunter win. */
  private fleeTimer = 0
  /** Counts down while it has noticed you but not yet committed. */
  private noticeTimer = 0
  /** How long it has been chasing, so it can lose interest. */
  private pursuitTimer = 0
  /** Which side it is trying to come round — animals rarely charge straight in. */
  private flank = 0
  private flankTimer = 0
  private readonly random: () => number
  private wanderTimer = 0

  constructor(id: string, speciesId: SpeciesId, x: number, z: number,
              difficulty: Difficulty = 'normal', seed = 1) {
    this.id = id
    this.speciesId = speciesId
    this.x = x
    this.z = z
    this.hearts = MAX_HEARTS * TOUGHNESS[difficulty]
    this.difficulty = difficulty

    let a = seed >>> 0
    this.random = () => {
      a = (a + 0x6d2b79f5) >>> 0
      let t = Math.imul(a ^ (a >>> 15), 1 | a)
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296
    }
    this.heading = this.random() * Math.PI * 2
    this.noticeTimer = NOTICE_DELAY[difficulty] * this.random()

    this.model = buildAnimal(speciesId)
    this.object.add(this.model.group)
    this.object.position.set(x, heightAt(x, z), z)
  }

  get species() {
    return SPECIES[this.speciesId]
  }

  /**
   * Wander, or hunt.
   *
   * `prey` is the player's position when hostile mobs are switched on (offline only).
   * Passing null keeps the old wandering behaviour, which is what online does.
   * Returns hearts of damage dealt to the player this step.
   */
  update(dt: number, prey?: { x: number; z: number } | null,
         others?: readonly AnimalAgent[],
         viewer?: { x: number; z: number }): number {
    if (this.isDead()) return 0

    let damage = 0

    // Who matters right now — could be the player, could be another animal entirely.
    const focus = this.chooseFocus(others ?? [], prey && this.preySpecies
      ? { x: prey.x, z: prey.z, species: this.preySpecies, hidden: this.preyHidden }
      : null)

    if (focus) {
      const dx = focus.x - this.x
      const dz = focus.z - this.z
      const distance = Math.hypot(dx, dz)
      // Whether this is a meal or a threat comes from the focus, which may be another
      // animal entirely rather than the player.
      const iHunt = !focus.threat
      const iFear = focus.threat
      // Loud things are noticed from further away; hidden things barely at all. The
      // hiding only applies to the player — animals do not freeze.
      const hidden = focus.agent === null ? 1 - this.preyHidden * 0.92 : 1
      const noticeRange = hidden * this.nightBoost * AGGRO_RANGE[this.difficulty] *
        Math.max(0.5, noticedRange(focus.species, 1)) *
        IDENTITY[this.speciesId].senses
      // Once committed it follows past where it first noticed you, rather than snapping
      // in and out of interest at an invisible boundary.
      const keepRange = noticeRange + PURSUIT_SLACK

      switch (this.mood) {
        case 'wander':
          this.fleeTimer = Math.max(0, this.fleeTimer - dt * 0.5)   // it gets its breath back
          if (iFear && distance < noticeRange * FLEE_TRIGGER) {
            // Run, don't approach. This is what makes being a bear feel different from
            // being a bunny: the same animals behave oppositely toward you.
            this.mood = 'fleeing'
            break
          }
          if (iHunt && distance < noticeRange) {
            this.mood = 'noticing'
            this.noticeTimer = NOTICE_DELAY[this.difficulty]
          }
          break

        case 'noticing':
          // Head up and hold still while it works out what you are. It must NOT keep
          // wandering here — it used to drift out of range mid-hesitation and lose you.
          this.noticeTimer -= dt
          this.turnToward(Math.atan2(dz, dx), dt, TURN_RATE * 0.6)
          if (distance > keepRange) this.mood = 'wander'
          else if (this.noticeTimer <= 0) {
            this.mood = 'chasing'
            this.pursuitTimer = 0
          }
          break

        case 'chasing': {
          // Patience does not run down while it is actually in contact. A predator with
          // its teeth in does not quit on a timer — without this, every hunt closed
          // perfectly, landed one bite, and then gave up on the same frame.
          if (distance > ATTACK_RANGE) this.pursuitTimer += dt
          // A well-hidden animal is lost even mid-chase. Without this, freezing after
          // you have been spotted does nothing, which defeats the point — a prey animal
          // has no other defence once a predator has committed.
          if (this.preyHidden > 0.7) {
            this.lostTimer += dt
            if (this.lostTimer > 0.8) {
              this.mood = 'bored'
              this.boredTimer = 4 + this.random() * 4
              this.lostTimer = 0
              break
            }
          } else {
            this.lostTimer = 0
          }
          if (distance > keepRange || this.pursuitTimer > PURSUIT_PATIENCE[this.difficulty]) {
            this.mood = 'bored'
            this.boredTimer = 3 + this.random() * 4
            break
          }
          // Come round at an angle rather than dead straight, swapping sides now and
          // then the way an animal circles something it is wary of.
          this.flankTimer -= dt
          if (this.flankTimer <= 0) {
            this.flank = (this.random() - 0.5) * 1.1
            this.flankTimer = 1.2 + this.random() * 2.2
          }
          // Ease the angle off as it closes, so the last stretch is a real approach.
          const closing = Math.min(1, distance / noticeRange)
          this.turnToward(Math.atan2(dz, dx) + this.flank * closing, dt, TURN_RATE)
          if (distance <= ATTACK_RANGE) {
            if (focus.agent) {
              // Animals actually kill each other, so the population genuinely shifts.
              focus.agent.applyDamage(IDENTITY[this.speciesId].role === 'apex' ? 6 * dt : 3.5 * dt)
              if (focus.agent.isDead()) { this.mood = 'bored'; this.boredTimer = 5 }
            } else {
              damage = AGGRO_DAMAGE[this.difficulty] * dt
            }
          }
          break
        }

        case 'fleeing': {
          // Head directly away, and keep going until there is real distance.
          this.fleeTimer += dt
          this.turnToward(Math.atan2(-dz, -dx), dt, TURN_RATE * 1.8)
          if (distance > keepRange * 1.3) { this.mood = 'wander'; this.fleeTimer = 0 }
          break
        }

        case 'bored':
          // Wanders off and will not re-notice you until it has cooled down. Staying
          // hidden keeps it bored.
          if (this.preyHidden > 0.7) this.boredTimer = Math.max(this.boredTimer, 1)
          this.boredTimer -= dt
          if (this.boredTimer <= 0) this.mood = 'wander'
          break
      }

      this.hunting = this.mood === 'chasing'
      // Publish the quarry so packmates can join, and clear it when not hunting.
      this.packTarget = this.hunting
        ? (focus.agent ?? {
            x: focus.x, z: focus.z, speciesId: focus.species,
            isPlayerProxy: true as const, isDead: () => false,
          })
        : null

      // Hold at arm's length instead of walking into the player and standing inside them.
      if (focus.agent === null && distance < STANDOFF && distance > 0.001) {
        this.x -= (dx / distance) * (STANDOFF - distance)
        this.z -= (dz / distance) * (STANDOFF - distance)
      }
    } else {
      this.hunting = false
      this.packTarget = null
      this.mood = 'wander'
    }

    // Only amble about when it is not interested in you. 'noticing' stands its ground
    // and 'fleeing' is already steering.
    if (this.mood === 'wander' || this.mood === 'bored') {

      this.wanderTimer -= dt
      if (this.wanderTimer <= 0) {
        this.heading += (this.random() - 0.5) * Math.PI
        this.wanderTimer = 2 + this.random() * 4
      }
    }

    // A hunting animal moves with intent; a wandering one ambles. Speed eases in over
    // the first second of a chase rather than jumping to full pace.
    const commitment = Math.min(1, this.pursuitTimer)
    const speed = this.mood === 'noticing'
      ? this.species.speed * 0.06          // near enough standing, head up, watching
      : this.mood === 'fleeing'
        // Full pelt at first, then flagging — a chase it cannot sustain forever.
        ? this.species.speed * (this.fleeTimer < STAMINA_SECONDS
            ? 1.0
            : TIRED_SPEED)
        // A committed hunter runs slightly harder than a tiring animal can keep up with.
        : this.species.speed * (this.hunting ? 0.62 + 0.5 * commitment : 0.55)
    this.x += Math.cos(this.heading) * speed * dt
    this.z += Math.sin(this.heading) * speed * dt
    // Push apart from any animal standing too close. Without this the whole pack
    // converges on the same point and renders as one pile of bodies.
    if (others) {
      for (const other of others) {
        if (other === this || other.isDead()) continue
        // Never push away from the thing being hunted. Personal space is 2.4m and the
        // bite range is 2.2m, so this rule was holding every predator exactly one step
        // outside its own kill — chases closed perfectly and never landed.
        if (this.mood === 'chasing' && other === focus?.agent) continue
        const ox = this.x - other.x
        const oz = this.z - other.z
        const gap = Math.hypot(ox, oz)
        if (gap > 0.001 && gap < PERSONAL_SPACE) {
          const push = (PERSONAL_SPACE - gap) * 0.5
          this.x += (ox / gap) * push
          this.z += (oz / gap) * push
        }
      }
    }

    const clamped = { x: this.x, z: this.z }
    if (clampToBorder(clamped)) this.heading += Math.PI
    this.x = clamped.x
    this.z = clamped.z
    // Stand on the land, whatever height it is here.
    this.object.position.set(this.x, heightAt(this.x, this.z), this.z)
    // Face where it's walking, and cycle its legs with the distance covered.
    this.object.rotation.y = -this.heading - Math.PI / 2
    this.stridePhase += speed * dt * 2.4
    // Wary animals crouch; a fleeing one runs flat out with its head up.
    animate(this.model, this.stridePhase, true, false, gaitFor(this.speciesId), {
      crouch: this.mood === 'noticing' ? 0.6 : 0,
      // Lunging while in contact with its quarry, and flinching when bitten.
      attack: this.mood === 'chasing' && damage > 0 ? 1 : 0,
      hurt: this.flinch,
    }, dt)
    this.flinch = Math.max(0, this.flinch - dt * 3)
    // Thin the coat with distance; fourteen shells per animal does not scale to a field.
    const from = viewer ?? prey
    if (from) updateFurDetail(this.model, Math.hypot(this.x - from.x, this.z - from.z))
    return damage
  }

  /**
   * Pick what this animal should care about right now: the nearest thing that hunts it,
   * or the nearest thing it hunts. A threat always wins — staying alive beats eating.
   *
   * This is what turns a field of wandering props into an ecosystem. A wolf chasing a
   * deer past you is a story nobody authored.
   */
  private chooseFocus(
    others: readonly AnimalAgent[],
    player: { x: number; z: number; species: SpeciesId | null; hidden: number } | null,
  ): Focus | null {
    // Gather candidates and pick afterwards. Assigning to captured locals inside a
    // closure defeats TypeScript's narrowing, which is why this is a list rather than
    // two running bests.
    const candidates: Array<Focus & { score: number }> = []

    const consider = (x: number, z: number, species: SpeciesId, agent: AnimalAgent | null,
                      hidden: number): void => {
      const threat = hunts(species, this.speciesId)
      const meal = hunts(this.speciesId, species)
      if (!threat && !meal) return
      // Hidden things are effectively further away.
      const score = Math.hypot(x - this.x, z - this.z) * (1 + hidden * 6)
      candidates.push({ x, z, species, agent, threat, score })
    }

    for (const other of others) {
      if (other === this || other.isDead()) continue
      consider(other.x, other.z, other.speciesId, other, 0)
    }
    if (player?.species) consider(player.x, player.z, player.species, null, player.hidden)

    // Join a packmate's hunt. Weighted as very attractive so the pack converges rather
    // than each wolf independently choosing the nearest thing it happens to see.
    if (PACK_SPECIES.has(this.speciesId)) {
      for (const other of others) {
        if (other === this || other.isDead()) continue
        if (!PACK_SPECIES.has(other.speciesId)) continue
        if (other.mood !== 'chasing' || !other.packTarget) continue
        if (Math.hypot(other.x - this.x, other.z - this.z) > PACK_RANGE) continue
        const target: PackTarget = other.packTarget
        if (target.isDead()) continue
        const isProxy = 'isPlayerProxy' in target
        candidates.push({
          x: target.x, z: target.z,
          species: isProxy ? target.speciesId : target.speciesId,
          agent: isProxy ? null : target,
          threat: false,
          // Strongly preferred, so the pack commits together.
          score: Math.hypot(target.x - this.x, target.z - this.z) * 0.3,
        })
      }
    }

    if (candidates.length === 0) return null
    // Staying alive beats eating, but only for things that are actually near. Ranking
    // threats absolutely above meals left every wolf fixated on a bear 2,860m away and
    // blind to the deer beside it — so threats merely count as closer than they are.
    candidates.sort((a, b) =>
      (a.threat ? a.score * THREAT_BIAS : a.score) -
      (b.threat ? b.score * THREAT_BIAS : b.score))
    const best = candidates[0]!
    return { x: best.x, z: best.z, species: best.species, agent: best.agent, threat: best.threat }
  }

  /** Rotate the heading toward `want`, never faster than `rate`. */
  private turnToward(want: number, dt: number, rate: number): void {
    let delta = want - this.heading
    while (delta > Math.PI) delta -= Math.PI * 2
    while (delta < -Math.PI) delta += Math.PI * 2
    this.heading += Math.max(-rate * dt, Math.min(rate * dt, delta))
  }

  /** Bolt away from wherever it is now — used when a bear tramples nearby. */
  scatter(): void {
    this.mood = 'fleeing'
    this.heading += (this.random() - 0.5) * 1.2
  }

  /** Decays after being bitten, driving the flinch. */
  flinch = 0

  applyDamage(amount: number): void {
    this.hearts = Math.max(0, this.hearts - amount)
    // Only flinch on a real hit, not on the per-frame trickle of a sustained bite.
    if (amount > 0.25) this.flinch = 1
  }

  kill(): void {
    this.hearts = 0
    this.object.visible = false
  }

  isDead(): boolean {
    return this.hearts <= 0
  }
}
